import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EyeOff, Lock, MoreHorizontal, Pencil, Search, Trash2, Users, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { MenuItem, MenuPopover, MenuSeparator } from '@/components/ui/Popover';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { FileHighlightFields } from '@/components/documents/FileHighlightFields';
import { HighlightTagPills } from '@/components/documents/HighlightTagFace';
import {
  highlightTagColorLabelKey,
  highlightTagDisplayName,
  highlightTagsInCatalogOrder,
  type HighlightTagColorSource,
} from '@/lib/highlightTagColors';
import { cn } from '@/utils/cn';
import { assignScrollTopToReveal } from '@/utils/previewScroll';
import {
  HIGHLIGHT_SEARCH_MIN,
  canDeleteHighlight,
  canEditHighlight,
  formatHighlightTime,
  groupHighlightsByPage,
  highlightColorCssVar,
  quotePreview,
  type HighlightColor,
  type HighlightFilter,
  type HighlightVisibility,
  type NumberedHighlight,
} from '@/utils/fileHighlights';

interface FileHighlightListProps {
  highlights: NumberedHighlight[];
  filtered: NumberedHighlight[];
  filter: HighlightFilter;
  query: string;
  counts: Record<HighlightFilter, number>;
  selectedId: string | null;
  hoveredId: string | null;
  editingId: string | null;
  userId: string | null | undefined;
  isClinicAdmin: boolean;
  authorNameByUserId: Record<string, string>;
  loading?: boolean;
  loadError?: boolean;
  hiddenNotice?: boolean;
  editColor: HighlightColor;
  editVisibility: HighlightVisibility;
  editComment: string;
  highlightTags?: readonly HighlightTagColorSource[];
  editTagIds?: readonly string[];
  saving?: boolean;
  saveError?: string | null;
  onFilter: (filter: HighlightFilter) => void;
  onQuery: (query: string) => void;
  onClearFilter: () => void;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onMenuOpen: (open: boolean) => void;
  onEdit: (id: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  onToggleVisibility: (id: string, visibility: HighlightVisibility) => void;
  onAskDelete: (id: string) => void;
  onEditColor: (color: HighlightColor) => void;
  onEditTagIds?: (ids: string[]) => void;
  onEditVisibility: (visibility: HighlightVisibility) => void;
  onEditComment: (comment: string) => void;
  onShowHighlights: () => void;
  onRetry: () => void;
}

export function FileHighlightList({
  highlights,
  filtered,
  filter,
  query,
  counts,
  selectedId,
  hoveredId,
  editingId,
  userId,
  isClinicAdmin,
  authorNameByUserId,
  loading = false,
  loadError = false,
  hiddenNotice = false,
  editColor,
  editVisibility,
  editComment,
  highlightTags = [],
  editTagIds = [],
  saving = false,
  saveError = null,
  onFilter,
  onQuery,
  onClearFilter,
  onSelect,
  onHover,
  onMenuOpen,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onToggleVisibility,
  onAskDelete,
  onEditColor,
  onEditTagIds,
  onEditVisibility,
  onEditComment,
  onShowHighlights,
  onRetry,
}: FileHighlightListProps) {
  const { t, i18n } = useTranslation();
  const listRef = useRef<HTMLUListElement>(null);
  const filteredIds = filtered.map((row) => row.id);
  const showSearch = highlights.length >= HIGHLIGHT_SEARCH_MIN;
  const filtering = filter !== 'all' || query.trim().length > 0;
  const groups = groupHighlightsByPage(filtered);
  const showGroups = groups.length > 1 || highlights.length >= HIGHLIGHT_SEARCH_MIN;

  useEffect(() => {
    if (!selectedId || !listRef.current) return;
    const node = listRef.current.querySelector(`[data-hl-id="${selectedId}"]`);
    if (node instanceof HTMLElement) assignScrollTopToReveal(listRef.current, node);
  }, [selectedId]);

  const filterLabel = (() => {
    switch (filter) {
      case 'all':
        return t('documents.highlights.filterAll');
      case 'mine':
        return t('documents.highlights.filterMine');
      case 'shared':
        return t('documents.highlights.filterShared');
      default: {
        const _never: never = filter;
        return _never;
      }
    }
  })();

  return (
    <section className="fp-list" aria-label={t('documents.highlights.list_title')}>
      {hiddenNotice ? (
        <div className="fp-hidden-notice">
          <EyeOff className="h-4 w-4" aria-hidden />
          <span>{t('documents.highlights.hiddenNotice')}</span>
          <Button type="button" variant="tertiary" size="sm" onClick={onShowHighlights}>
            {t('documents.highlights.show')}
          </Button>
        </div>
      ) : null}
      <div className="fp-list-head">
        <div className="fp-list-title-row">
          <h2>{t('documents.highlights.list_title')}</h2>
          <span className="fp-count-chip">{highlights.length}</span>
        </div>
        {showSearch ? (
          <Input
            leadingIcon={<Search className="h-4 w-4" aria-hidden />}
            value={query}
            onChange={(event) => onQuery(event.target.value)}
            placeholder={t('documents.highlights.search')}
            aria-label={t('documents.highlights.search')}
          />
        ) : null}
        <SegmentedControl
          ariaLabel={t('documents.highlights.list_title')}
          value={filter}
          onChange={onFilter}
          options={[
            { value: 'all', label: t('documents.highlights.filterAll'), count: counts.all },
            { value: 'mine', label: t('documents.highlights.filterMine'), count: counts.mine },
            {
              value: 'shared',
              label: t('documents.highlights.filterShared'),
              count: counts.shared,
            },
          ]}
        />
        {filtering ? (
          <div className="fp-filter-stat">
            <span>
              {t('documents.highlights.showingOf', {
                shown: filtered.length,
                total: highlights.length,
                filter: filterLabel,
              })}
            </span>
            <Button type="button" variant="tertiary" size="sm" onClick={onClearFilter}>
              <X className="h-3.5 w-3.5" aria-hidden />
              {t('documents.highlights.clearFilter')}
            </Button>
          </div>
        ) : null}
      </div>
      {loading ? <p className="fp-list-state">{t('documents.highlights.loading')}</p> : null}
      {loadError ? (
        <div className="fp-list-state">
          <p>{t('documents.highlights.loadError')}</p>
          <Button type="button" variant="tertiary" size="sm" onClick={onRetry}>
            {t('documents.highlights.retry')}
          </Button>
        </div>
      ) : null}
      {!loading && !loadError && highlights.length === 0 ? (
        <p className="fp-empty">{t('documents.highlights.empty')}</p>
      ) : null}
      {!loading && !loadError && highlights.length > 0 && filtered.length === 0 ? (
        <div className="fp-list-state">
          <p>{t('documents.highlights.noMatches')}</p>
          <Button type="button" variant="tertiary" size="sm" onClick={onClearFilter}>
            {t('documents.highlights.clearFilter')}
          </Button>
        </div>
      ) : null}
      <ul
        ref={listRef}
        className="fp-list-body"
        tabIndex={filtered.length > 0 ? 0 : undefined}
        onKeyDown={(event) => {
          if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp' && event.key !== 'Enter') return;
          if (filteredIds.length === 0) return;
          event.preventDefault();
          const current = selectedId ? filteredIds.indexOf(selectedId) : -1;
          if (event.key === 'Enter' && selectedId) {
            onSelect(selectedId);
            return;
          }
          const delta = event.key === 'ArrowDown' ? 1 : -1;
          const next =
            current < 0
              ? event.key === 'ArrowDown'
                ? 0
                : filteredIds.length - 1
              : (current + delta + filteredIds.length) % filteredIds.length;
          onSelect(filteredIds[next]);
        }}
      >
        {groups.map((group) => (
          <li key={`page-${group.page}`}>
            {showGroups ? (
              <div className="fp-list-group">
                <span>{t('documents.highlights.pageGroup', { page: group.page })}</span>
                <span>
                  {t('documents.highlights.pageGroupCount', { count: group.items.length })}
                </span>
              </div>
            ) : null}
            <ul>
              {group.items.map((highlight) => {
                const author =
                  highlight.author_name ||
                  authorNameByUserId[highlight.created_by] ||
                  t('documents.highlights.unknown_author');
                const time = formatHighlightTime(highlight.created_at, i18n.language);
                const canEdit = canEditHighlight({ highlight, userId });
                const canDelete = canDeleteHighlight({ highlight, userId, isClinicAdmin });
                const editing = editingId === highlight.id;
                const linkedTags = highlightTagsInCatalogOrder(highlightTags, highlight.tagIds);
                const linkedLabel = linkedTags
                  .map((tag) =>
                    highlightTagDisplayName(
                      tag,
                      t('settings.highlight_tags.unnamed'),
                      t(highlightTagColorLabelKey(tag.color_token)),
                    ),
                  )
                  .join(', ');
                const quoteLabel = quotePreview(highlight.quote);
                return (
                  <li
                    key={highlight.id}
                    data-hl-id={highlight.id}
                    className={cn(
                      'fp-list-item',
                      selectedId === highlight.id && !editing && 'is-on',
                      hoveredId === highlight.id && selectedId !== highlight.id && 'is-hov',
                      editing && 'is-editing',
                    )}
                    onMouseEnter={() => onHover(highlight.id)}
                    onMouseLeave={() => onHover(null)}
                  >
                    <button
                      type="button"
                      className="fp-list-hit"
                      aria-current={selectedId === highlight.id}
                      aria-label={linkedLabel ? `${quoteLabel}. ${linkedLabel}` : quoteLabel}
                      onClick={() => onSelect(highlight.id)}
                    />
                    <div className="fp-list-top">
                      <span
                        className="fp-badge"
                        style={{ ['--hc' as string]: highlightColorCssVar(highlight.color) }}
                        aria-hidden
                      >
                        {highlight.number}
                      </span>
                      <span className="fp-list-quote">{quoteLabel}</span>
                      {canEdit || canDelete ? (
                        <span className="fp-list-more">
                          <MenuPopover
                            align="end"
                            title={t('documents.highlights.moreActions')}
                            trigger={
                              <button
                                type="button"
                                className="fp-icon-btn is-sm"
                                aria-label={t('documents.highlights.moreActions')}
                                onClick={() => onMenuOpen(true)}
                              >
                                <MoreHorizontal className="h-4 w-4" />
                              </button>
                            }
                          >
                            {canEdit ? (
                              <>
                                <MenuItem
                                  icon={<Pencil className="h-4 w-4" />}
                                  onClick={() => {
                                    onMenuOpen(false);
                                    onEdit(highlight.id);
                                  }}
                                >
                                  {t('documents.highlights.menuEdit')}
                                </MenuItem>
                                <MenuItem
                                  icon={
                                    highlight.visibility === 'clinic' ? (
                                      <Lock className="h-4 w-4" />
                                    ) : (
                                      <Users className="h-4 w-4" />
                                    )
                                  }
                                  onClick={() => {
                                    onMenuOpen(false);
                                    onToggleVisibility(
                                      highlight.id,
                                      highlight.visibility === 'clinic' ? 'private' : 'clinic',
                                    );
                                  }}
                                >
                                  {highlight.visibility === 'clinic'
                                    ? t('documents.highlights.makePrivate')
                                    : t('documents.highlights.share')}
                                </MenuItem>
                                <MenuSeparator />
                              </>
                            ) : null}
                            {canDelete ? (
                              <MenuItem
                                danger
                                icon={<Trash2 className="h-4 w-4" />}
                                onClick={() => {
                                  onMenuOpen(false);
                                  onAskDelete(highlight.id);
                                }}
                              >
                                {t('documents.highlights.delete')}
                              </MenuItem>
                            ) : null}
                          </MenuPopover>
                        </span>
                      ) : null}
                    </div>
                    {editing ? (
                      <div className="fp-list-edit">
                        <FileHighlightFields
                          commentId={`fp-edit-comment-${highlight.id}`}
                          color={editColor}
                          visibility={editVisibility}
                          comment={editComment}
                          disabled={saving}
                          tags={highlightTags}
                          selectedTagIds={editTagIds}
                          onColor={onEditColor}
                          onSelectedTagIds={onEditTagIds}
                          onVisibility={onEditVisibility}
                          onComment={onEditComment}
                        />
                        <div className="fp-edit-actions">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={onCancelEdit}
                            disabled={saving}
                          >
                            {t('common.cancel')}
                          </Button>
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            loading={saving}
                            onClick={onSaveEdit}
                          >
                            {t('documents.highlights.saveChanges')}
                          </Button>
                        </div>
                        {saveError ? <p className="fp-form-error">{saveError}</p> : null}
                      </div>
                    ) : (
                      <>
                        <HighlightTagPills tags={highlightTags} tagIds={highlight.tagIds} />
                        {highlight.comment ? (
                          <p className="fp-list-comment">{highlight.comment}</p>
                        ) : null}
                        <div className="fp-list-meta">
                          <span>
                            {author} · <span className="num">{time}</span>
                          </span>
                          <span
                            className={cn(
                              'inline-flex h-[22px] items-center gap-1 rounded-full px-2 text-[12px] font-semibold',
                              highlight.visibility === 'clinic'
                                ? 'bg-ds-teal-tint text-ds-teal'
                                : 'bg-ds-neutral-tint text-ds-muted',
                            )}
                          >
                            {highlight.visibility === 'clinic' ? (
                              <Users className="h-3.5 w-3.5" aria-hidden />
                            ) : (
                              <Lock className="h-3.5 w-3.5" aria-hidden />
                            )}
                            {highlight.visibility === 'clinic'
                              ? t('documents.highlights.shared_short')
                              : t('documents.highlights.private')}
                          </span>
                        </div>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}
